import { unitIconById } from '@/components/common/unit-icons'
import JobClusterPin from './icons/job-cluster-pin'
import CirclePin from './icons/circle-pin'
import OwnedLocationPin from './icons/owned-location-pin'
import PartnerLocationPin from './icons/partner-location-pin'

export const ARROW_IMAGE_IDS = [
  'arrow-active',
  'arrow-active-op',
  'arrow-active-noop',
  'arrow-standby',
  'arrow-standby-op',
  'arrow-standby-noop',
  'arrow-offline',
  'arrow-offline-op',
  'arrow-offline-noop',
] as const

type ImageCache = Map<string, Promise<void>>
type GeneratedImageFactory = (id: string) => mapboxgl.StyleImageInterface

const GENERATED_IMAGE_FACTORIES: [prefix: string, factory: GeneratedImageFactory][] = [
  ['job-cluster', JobClusterPin],
  ['circle', CirclePin],
  ['owned', OwnedLocationPin],
  ['partner', PartnerLocationPin],
]

const loadMapImage = (map: mapboxgl.Map, id: string, src: string): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    map.loadImage(src, (error, image) => {
      if (!error && image) {
        map.addImage(id, image, { pixelRatio: 4 })
        resolve(true)
        return
      }

      resolve(false)
    })
  })
}

const importArrowImage = async (id: string): Promise<string> => {
  const src = unitIconById(id)
  if (!src) {
    // Preserves the old behaviour of a failed dynamic import: the caller's catch
    // evicts the id from the cache so a later attempt can retry.
    throw new Error(`Unknown unit icon: ${id}`)
  }
  return src
}

export function useMapImages(map: mapboxgl.Map) {
  const imageCache: ImageCache = new Map()

  const loadArrowImage = async (id: string): Promise<void> => {
    try {
      const src = await importArrowImage(id)
      const loaded = await loadMapImage(map, id, src)

      if (!loaded) {
        imageCache.delete(id)
      }
    } catch {
      imageCache.delete(id)
    }
  }

  const addGeneratedImage = (id: string): boolean => {
    const imageFactory = GENERATED_IMAGE_FACTORIES.find(([prefix]) => id.startsWith(prefix))?.[1]

    if (!imageFactory) {
      return false
    }

    map.addImage(id, imageFactory(id), { pixelRatio: 4 })
    return true
  }

  const ensureImage = (id: string): Promise<void> => {
    if (map.hasImage(id)) {
      return Promise.resolve()
    }

    if (addGeneratedImage(id)) {
      return Promise.resolve()
    }

    if (id.startsWith('arrow')) {
      if (!imageCache.has(id)) {
        imageCache.set(id, loadArrowImage(id))
      }
      return imageCache.get(id)!
    }

    return Promise.resolve()
  }

  const ensureImages = async (ids: readonly string[]): Promise<void> => {
    await Promise.all(ids.map((id) => ensureImage(id)))
  }

  const ensureArrowImages = async (): Promise<void> => {
    await ensureImages(ARROW_IMAGE_IDS)
  }

  const registerMissingImageHandler = () => {
    // Only handles synchronously-generated images (job-cluster, circle, owned, partner).
    // Arrow images require async loading which styleimagemissing does not support —
    // they must be preloaded via ensureImages before map rendering.
    map.on('styleimagemissing', ({ id }) => {
      if (!map.hasImage(id)) {
        addGeneratedImage(id)
      }
    })
  }

  return {
    ensureArrowImages,
    ensureImages,
    registerMissingImageHandler,
  }
}
